import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantContext } from '../../../context/TenantContext';
import { useToast } from '../../../hooks/useToast';
import { supabase } from '../../../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { useCheckPermissions } from '../../../hooks/useCheckPermissions';
import { 
  BookOpen, 
  Search, 
  Filter, 
  ArrowUpDown,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  UserCheck,
  Calendar,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus
} from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Badge } from '../../../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';

const AdminEnrollmentsPage: React.FC = () => {
  useCheckPermissions(['admin']);
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('enrolled_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedEnrollment, setSelectedEnrollment] = useState<any>(null);
  const [isViewDetailsDialogOpen, setIsViewDetailsDialogOpen] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isStudentDetailsDialogOpen, setIsStudentDetailsDialogOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [studentDetails, setStudentDetails] = useState<any>(null);
  const [isAddEnrollmentDialogOpen, setIsAddEnrollmentDialogOpen] = useState(false);
  const [newEnrollmentData, setNewEnrollmentData] = useState({
    student_id: '',
    course_id: '',
    status: 'active'
  });
  const [availableStudents, setAvailableStudents] = useState<any[]>([]);
  const [availableCourses, setAvailableCourses] = useState<any[]>([]);
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [enrollmentsPerPage] = useState(10);

  useEffect(() => {
    const { tenantId } = getDomainFilterParams();
    if (tenantId) {
      fetchEnrollments();
    }
  }, [sortBy, sortOrder, statusFilter]);

  const fetchEnrollments = async () => {
    const { tenantId } = getDomainFilterParams();
    if (!tenantId) return;
    
    setLoading(true);
    try {
      // Récupérer les inscriptions depuis Supabase
      let query = supabase
        .from('elearn_enrollments')
        .select(`
          id,
          student_id,
          enrolled_at,
          completed_at,
          status,
          progress_percentage,
          last_accessed_at,
          certificate_url,
          course:course_id(id, title, thumbnail_url)
        `);
      
      query = applyDomainFilterToQuery(query);
      const { data: enrollmentsData, error: enrollmentsError } = await query
        .order(sortBy, { ascending: sortOrder === 'asc' });

      if (enrollmentsError) throw enrollmentsError;
      
      // Si aucune inscription n'est trouvée, utiliser des données simulées pour la démonstration
      if (!enrollmentsData || enrollmentsData.length === 0) {
        const mockEnrollments = [
          {
            id: '1',
            student_name: 'Jean Dupont',
            student_email: 'jean.dupont@example.com',
            course: { title: 'Introduction à la Photographie' },
            enrolled_at: '2025-06-15T10:30:00Z',
            completed_at: null,
            status: 'active',
            progress_percentage: 35,
          },
          {
            id: '2',
            student_name: 'Marie Martin',
            student_email: 'marie.martin@example.com',
            course: { title: 'Techniques Avancées de Dessin' },
            enrolled_at: '2025-06-10T14:20:00Z',
            completed_at: '2025-07-05T09:15:00Z',
            status: 'completed',
            progress_percentage: 100,
          },
          {
            id: '3',
            student_name: 'Pierre Durand',
            student_email: 'pierre.durand@example.com',
            course: { title: 'Bases de la Composition' },
            enrolled_at: '2025-06-20T08:45:00Z',
            completed_at: null,
            status: 'active',
            progress_percentage: 65,
          },
        ];
        
        setEnrollments(mockEnrollments);
      } else {
        // Récupérer les informations des étudiants
        const studentIds = enrollmentsData.map(enrollment => enrollment.student_id);
        let studentsQuery = supabase
          .from('elearn_customer_profiles')
          .select('id, full_name, email')
          .in('id', studentIds);
        
        studentsQuery = applyDomainFilterToQuery(studentsQuery);
        const { data: studentsData } = await studentsQuery;

        // Combiner les données des inscriptions avec les données des étudiants
        const enrichedEnrollments = enrollmentsData.map(enrollment => {
          const student = studentsData?.find(s => s.id === enrollment.student_id) || { full_name: 'Étudiant inconnu', email: 'inconnu@example.com' };
          return {
            ...enrollment,
            student_name: student.full_name,
            student_email: student.email,
            course_title: enrollment.course && typeof enrollment.course === 'object' ? enrollment.course.title : 'Cours inconnu'
          };
        });

        setEnrollments(enrichedEnrollments);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des inscriptions:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les inscriptions',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentDetails = async (studentId: string) => {
    try {
      setStudentDetails(null); // Réinitialiser les détails précédents
      
      const { data, error } = await supabase
        .from('elearn_customer_profiles')
        .select('*')
        .eq('id', studentId)
        .single();

      if (error) throw error;
      
      if (data) {
        setStudentDetails(data);
        setIsStudentDetailsDialogOpen(true);
      } else {
        toast({
          title: 'Information',
          description: 'Aucune information supplémentaire disponible pour cet étudiant',
        });
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des détails de l\'étudiant:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer les informations de l\'étudiant',
        variant: 'destructive',
      });
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Réinitialiser la page courante lors d'une recherche
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1); // Réinitialiser la page courante lors d'un changement de filtre
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const filteredEnrollments = enrollments.filter(enrollment => {
    const matchesSearch = 
      enrollment.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enrollment.student_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enrollment.course_title.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || enrollment.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });
  
  // Réinitialiser la page courante lorsque les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, sortBy, sortOrder]);
  
  // Calculer les indices des inscriptions à afficher sur la page courante
  const indexOfLastEnrollment = currentPage * enrollmentsPerPage;
  const indexOfFirstEnrollment = indexOfLastEnrollment - enrollmentsPerPage;
  const currentEnrollments = filteredEnrollments.slice(indexOfFirstEnrollment, indexOfLastEnrollment);
  
  // Fonction pour changer de page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const totalPages = Math.ceil(filteredEnrollments.length / enrollmentsPerPage);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-blue-500">En cours</Badge>;
      case 'completed':
        return <Badge className="bg-green-500">Terminé</Badge>;
      case 'suspended':
        return <Badge className="bg-amber-500">Suspendu</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-500">Annulé</Badge>;
      default:
        return <Badge className="bg-gray-500">Inconnu</Badge>;
    }
  };

  const handleDeleteEnrollment = async (enrollmentId: string) => {
    const { tenantId } = getDomainFilterParams();
    if (!tenantId) return;
    
    try {
      // Appel à Supabase pour supprimer l'inscription
      let deleteQuery = supabase
        .from('elearn_enrollments')
        .delete()
        .eq('id', enrollmentId);
      
      deleteQuery = applyDomainFilterToQuery(deleteQuery);
      const { error } = await deleteQuery;
      
      if (error) throw error;
      
      toast({
        title: "Inscription supprimée",
        description: "L'inscription a été supprimée avec succès.",
      });
      
      // Mise à jour de l'état local pour refléter la suppression
      setEnrollments(enrollments.filter(enrollment => enrollment.id !== enrollmentId));
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'inscription:', error);
      toast({
        title: "Erreur",
        description: "Une erreur s'est produite lors de la suppression de l'inscription.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateStatus = async (enrollmentId: string, newStatus: string) => {
    const { tenantId } = getDomainFilterParams();
    if (!tenantId) return;
    
    try {
      // Appel à Supabase pour mettre à jour le statut
      let updateQuery = supabase
        .from('elearn_enrollments')
        .update({ 
          status: newStatus,
          // Si le statut est 'completed', mettre à jour la date de complétion
          ...(newStatus === 'completed' ? { completed_at: new Date().toISOString() } : {})
        })
        .eq('id', enrollmentId);
      
      updateQuery = applyDomainFilterToQuery(updateQuery);
      const { error } = await updateQuery;
      
      if (error) throw error;
      
      toast({
        title: "Statut mis à jour",
        description: `Le statut de l'inscription a été mis à jour vers "${newStatus}".`,
      });
      
      // Mise à jour de l'état local pour refléter le changement de statut
      setEnrollments(enrollments.map(enrollment => {
        if (enrollment.id === enrollmentId) {
          return { 
            ...enrollment, 
            status: newStatus,
            completed_at: newStatus === 'completed' ? new Date().toISOString() : enrollment.completed_at
          };
        }
        return enrollment;
      }));
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      toast({
        title: "Erreur",
        description: "Une erreur s'est produite lors de la mise à jour du statut.",
        variant: "destructive",
      });
    }
  };

  const handleAddEnrollment = async () => {
    const { tenantId } = getDomainFilterParams();
    if (!tenantId) return;
    
    try {
      // Réinitialiser les données du formulaire
      setNewEnrollmentData({
        student_id: '',
        course_id: '',
        status: 'active'
      });
      
      // Récupérer la liste des étudiants disponibles
      let studentsQuery = supabase
        .from('elearn_customer_profiles')
        .select('id, full_name, email');
      
      studentsQuery = applyDomainFilterToQuery(studentsQuery);
      const { data: studentsData, error: studentsError } = await studentsQuery;
      
      if (studentsError) throw studentsError;
      
      // Récupérer la liste des cours disponibles
      let coursesQuery = supabase
        .from('elearn_courses')
        .select('id, title');
      
      coursesQuery = applyDomainFilterToQuery(coursesQuery);
      const { data: coursesData, error: coursesError } = await coursesQuery;
      
      if (coursesError) throw coursesError;
      
      // Mettre à jour les états avec les données récupérées
      setAvailableStudents(studentsData || []);
      setAvailableCourses(coursesData || []);
      
      // Ouvrir la boîte de dialogue
      setIsAddEnrollmentDialogOpen(true);
    } catch (error) {
      console.error('Erreur lors de la récupération des données:', error);
      toast({
        title: "Erreur",
        description: "Une erreur s'est produite lors de la préparation du formulaire d'inscription.",
        variant: "destructive",
      });
    }
  };
  
  const handleCreateEnrollment = async () => {
    const { tenantId, domain } = getDomainFilterParams();
    console.log('handleCreateEnrollment appelé', { newEnrollmentData, tenantId, domain });
    
    if (!tenantId) {
      console.error('Tenant ID manquant');
      return;
    }
    
    // Validation des données
    if (!newEnrollmentData.student_id || !newEnrollmentData.course_id) {
      console.log('Validation échouée: données manquantes', newEnrollmentData);
      toast({
        title: "Validation échouée",
        description: "Veuillez sélectionner un étudiant et un cours.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      console.log('Vérification si l\'inscription existe déjà');
      // Vérifier si l'inscription existe déjà
      let checkQuery = supabase
        .from('elearn_enrollments')
        .select('id')
        .eq('student_id', newEnrollmentData.student_id)
        .eq('course_id', newEnrollmentData.course_id);
      
      checkQuery = applyDomainFilterToQuery(checkQuery);
      const { data: existingEnrollment, error: checkError } = await checkQuery.maybeSingle();
      
      console.log('Résultat de la vérification:', { existingEnrollment, checkError });
      
      if (checkError) {
        console.error('Erreur lors de la vérification:', checkError);
        throw checkError;
      }
      
      if (existingEnrollment) {
        console.log('Inscription existante trouvée');
        toast({
          title: "Inscription existante",
          description: "Cet étudiant est déjà inscrit à ce cours.",
          variant: "destructive",
        });
        return;
      }
      
      console.log('Aucune inscription existante trouvée, création d\'une nouvelle inscription');
      
      // Créer la nouvelle inscription
      console.log('Génération d\'un nouvel ID d\'inscription');
      const newEnrollmentId = uuidv4();
      console.log('Nouvel ID généré:', newEnrollmentId);
      
      const newEnrollmentRecord = {
        id: newEnrollmentId,
        tenant_id: tenantId,
        domain: domain,
        student_id: newEnrollmentData.student_id,
        course_id: newEnrollmentData.course_id,
        status: newEnrollmentData.status,
        enrolled_at: new Date().toISOString(),
        progress_percentage: 0
      };
      
      console.log('Tentative d\'insertion dans la base de données:', newEnrollmentRecord);
      
      const { error } = await supabase
        .from('elearn_enrollments')
        .insert(newEnrollmentRecord);
      
      console.log('Résultat de l\'insertion:', { error });
      
      if (error) {
        console.error('Erreur lors de l\'insertion:', error);
        throw error;
      }
      
      console.log('Inscription créée avec succès');
      
      // Trouver les détails de l'étudiant et du cours pour l'affichage
      console.log('Recherche des détails de l\'\u00e9tudiant et du cours');
      console.log('Liste des étudiants disponibles:', availableStudents);
      console.log('Liste des cours disponibles:', availableCourses);
      
      const student = availableStudents.find(s => s.id === newEnrollmentData.student_id);
      const course = availableCourses.find(c => c.id === newEnrollmentData.course_id);
      
      console.log('Étudiant trouvé:', student);
      console.log('Cours trouvé:', course);
      
      // Ajouter la nouvelle inscription à l'état local
      const newEnrollment = {
        id: newEnrollmentId,
        student_id: newEnrollmentData.student_id,
        student_name: student?.full_name || 'Étudiant inconnu',
        student_email: student?.email || 'inconnu@example.com',
        course_id: newEnrollmentData.course_id,
        course: { title: course?.title || 'Cours inconnu' },
        course_title: course?.title || 'Cours inconnu',
        status: newEnrollmentData.status,
        enrolled_at: new Date().toISOString(),
        completed_at: null,
        progress_percentage: 0,
        last_accessed_at: null,
        certificate_url: null
      };
      
      console.log('Nouvel objet inscription pour l\'UI:', newEnrollment);
      console.log('Mise à jour de l\'\u00e9tat local avec la nouvelle inscription');
      
      setEnrollments([...enrollments, newEnrollment]);
      
      console.log('Affichage du toast de confirmation');
      toast({
        title: "Inscription créée",
        description: `L'étudiant a été inscrit au cours avec succès.`,
      });
      
      console.log('Fermeture de la boîte de dialogue');
      // Fermer la boîte de dialogue
      setIsAddEnrollmentDialogOpen(false);
    } catch (error) {
      console.error('Erreur lors de la création de l\'inscription:', error);
      toast({
        title: "Erreur",
        description: "Une erreur s'est produite lors de la création de l'inscription.",
        variant: "destructive",
      });
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <UserCheck className="h-8 w-8 text-[#D4AF37]" />
              Gestion des Inscriptions
            </h1>
            <p className="text-gray-400 mt-2">
              {filteredEnrollments.length} inscription{filteredEnrollments.length > 1 ? 's' : ''} trouvée{filteredEnrollments.length > 1 ? 's' : ''}
            </p>
          </div>
          <Button 
            onClick={() => handleAddEnrollment()}
            className="bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white hover:opacity-90"
          >
            <Plus className="mr-2" size={18} />
            Nouvelle inscription
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Total</div>
            <div className="text-2xl font-bold text-white">{enrollments.length}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Actives</div>
            <div className="text-2xl font-bold text-blue-400">
              {enrollments.filter(e => e.status === 'active').length}
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Terminées</div>
            <div className="text-2xl font-bold text-green-400">
              {enrollments.filter(e => e.status === 'completed').length}
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Taux complétion</div>
            <div className="text-2xl font-bold text-[#D4AF37]">
              {enrollments.length > 0 
                ? Math.round(enrollments.reduce((sum, e) => sum + e.progress_percentage, 0) / enrollments.length) 
                : 0}%
            </div>
          </div>
        </div>

        {/* Filtres et recherche */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                placeholder="Rechercher par étudiant, cours..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400"
              />
            </div>

            {/* Filtre Statut */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 text-white rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50"
              >
                <option value="all" className="bg-gray-900">Tous statuts</option>
                <option value="active" className="bg-gray-900">En cours</option>
                <option value="completed" className="bg-gray-900">Terminés</option>
                <option value="suspended" className="bg-gray-900">Suspendus</option>
                <option value="cancelled" className="bg-gray-900">Annulés</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table des inscriptions */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
            </div>
          ) : filteredEnrollments.length === 0 ? (
            <div className="text-center py-12">
              <UserCheck className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Aucune inscription trouvée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('student_name')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Étudiant
                        <ArrowUpDown size={14} className={sortBy === 'student_name' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('course_title')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Cours
                        <ArrowUpDown size={14} className={sortBy === 'course_title' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('enrolled_at')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Date inscription
                        <ArrowUpDown size={14} className={sortBy === 'enrolled_at' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('progress_percentage')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Progression
                        <ArrowUpDown size={14} className={sortBy === 'progress_percentage' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('status')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Statut
                        <ArrowUpDown size={14} className={sortBy === 'status' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {currentEnrollments.map((enrollment, index) => (
                    <tr
                      key={enrollment.id}
                      className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                            <UserCheck className="h-5 w-5 text-[#D4AF37]" />
                          </div>
                          <div className="ml-4">
                            <div 
                              className="text-sm font-medium text-white cursor-pointer hover:text-[#D4AF37] transition-colors flex items-center gap-1" 
                              onClick={() => fetchStudentDetails(enrollment.student_id)}
                            >
                              {enrollment.student_name}
                              <Eye className="h-3 w-3 opacity-50" />
                            </div>
                            <div className="text-sm text-gray-400">{enrollment.student_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">{enrollment.course?.title || enrollment.course_title}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{formatDate(enrollment.enrolled_at)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-white/10 rounded-full h-2">
                            <div 
                              className="bg-[#D4AF37] h-2 rounded-full" 
                              style={{ width: `${enrollment.progress_percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-white">{enrollment.progress_percentage}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(enrollment.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 text-white">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-gray-900 text-white border-white/10">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuItem 
                              className="cursor-pointer hover:bg-white/10"
                              onClick={() => {
                                setSelectedEnrollment(enrollment);
                                setIsViewDetailsDialogOpen(true);
                              }}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              <span>Voir les détails</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="cursor-pointer hover:bg-white/10"
                              onClick={() => {
                                setSelectedEnrollment(enrollment);
                                setNewStatus(enrollment.status);
                                setIsStatusDialogOpen(true);
                              }}
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              <span>Modifier le statut</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuItem 
                              className="cursor-pointer text-red-500 hover:bg-white/10 hover:text-red-500"
                              onClick={() => {
                                setSelectedEnrollment(enrollment);
                                setIsDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              <span>Supprimer</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Contrôles de pagination */}
          {filteredEnrollments.length > 0 && (
              <div className="flex justify-between items-center p-4 border-t border-white/10">
                <div className="text-sm text-white/70">
                  Inscriptions ({filteredEnrollments.length}) - Page {currentPage}/{totalPages}
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => paginate(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="border-white/10 text-white hover:bg-white/5"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // Afficher les pages autour de la page courante
                    let pageToShow;
                    if (totalPages <= 5) {
                      // Si moins de 5 pages, afficher toutes les pages
                      pageToShow = i + 1;
                    } else if (currentPage <= 3) {
                      // Si on est au début, afficher les 5 premières pages
                      pageToShow = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      // Si on est à la fin, afficher les 5 dernières pages
                      pageToShow = totalPages - 4 + i;
                    } else {
                      // Sinon, afficher 2 pages avant et 2 pages après la page courante
                      pageToShow = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageToShow}
                        variant={currentPage === pageToShow ? "default" : "outline"}
                        size="sm"
                        onClick={() => paginate(pageToShow)}
                        className={currentPage === pageToShow 
                          ? "bg-blue-600 hover:bg-blue-700 text-white" 
                          : "border-white/10 text-white hover:bg-white/5"}
                      >
                        {pageToShow}
                      </Button>
                    );
                  })}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="border-white/10 text-white hover:bg-white/5"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
          )}
        </div>
      </div>

      {/* Boîte de dialogue pour voir les détails */}
      <Dialog open={isViewDetailsDialogOpen} onOpenChange={setIsViewDetailsDialogOpen}>
        <DialogContent className="bg-gray-900 text-white border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle>Détails de l'inscription</DialogTitle>
            <DialogDescription className="text-white/70">
              Informations complètes sur l'inscription de l'étudiant.
            </DialogDescription>
          </DialogHeader>
          
          {selectedEnrollment && (
            <div className="space-y-6">
              {/* Informations principales */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-black/20 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3 text-red-400">Informations de l'étudiant</h3>
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-medium text-white/70">Nom complet</h4>
                      <p className="text-white text-lg">{selectedEnrollment.student_name}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-white/70">Email</h4>
                      <p className="text-white">{selectedEnrollment.student_email}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-white/70">ID Étudiant</h4>
                      <p className="text-white text-xs font-mono">{selectedEnrollment.student_id}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/20 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3 text-red-400">Informations du cours</h3>
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-medium text-white/70">Titre du cours</h4>
                      <p className="text-white text-lg">{selectedEnrollment.course?.title || selectedEnrollment.course_title}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-white/70">ID du cours</h4>
                      <p className="text-white text-xs font-mono">{selectedEnrollment.course_id}</p>
                    </div>
                    {selectedEnrollment.course?.thumbnail_url && (
                      <div className="mt-2">
                        <img 
                          src={selectedEnrollment.course.thumbnail_url} 
                          alt={selectedEnrollment.course.title} 
                          className="w-full h-24 object-cover rounded-md"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Progression et statut */}
              <div className="bg-black/20 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-red-400">Progression et statut</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-white/70">Statut actuel</h4>
                    <div className="mt-1">{getStatusBadge(selectedEnrollment.status)}</div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-white/70">Progression globale</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-full bg-white/10 rounded-full h-2.5">
                        <div 
                          className="bg-blue-600 h-2.5 rounded-full" 
                          style={{ width: `${selectedEnrollment.progress_percentage}%` }}
                        ></div>
                      </div>
                      <span className="font-semibold">{selectedEnrollment.progress_percentage}%</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Dates importantes */}
              <div className="bg-black/20 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-red-400">Chronologie</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-white/70">Date d'inscription</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar size={16} className="text-blue-400" />
                      <p className="text-white">{formatDate(selectedEnrollment.enrolled_at)}</p>
                    </div>
                  </div>
                  
                  {selectedEnrollment.last_accessed_at && (
                    <div>
                      <h4 className="text-sm font-medium text-white/70">Dernier accès</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock size={16} className="text-yellow-400" />
                        <p className="text-white">{formatDate(selectedEnrollment.last_accessed_at)}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedEnrollment.completed_at && (
                    <div>
                      <h4 className="text-sm font-medium text-white/70">Date de complétion</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <CheckCircle size={16} className="text-green-400" />
                        <p className="text-white">{formatDate(selectedEnrollment.completed_at)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Certificat et actions */}
              <div className="bg-black/20 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-red-400">Certificat et actions</h3>
                <div className="flex flex-wrap gap-3">
                  {selectedEnrollment.certificate_url ? (
                    <Button
                      variant="outline"
                      className="border-green-500/30 text-green-400 hover:bg-green-500/20"
                      onClick={() => window.open(selectedEnrollment.certificate_url, '_blank')}
                    >
                      <CheckCircle size={16} className="mr-2" />
                      Voir le certificat
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="border-white/10 text-white/70 hover:bg-white/10"
                      disabled
                    >
                      <Clock size={16} className="mr-2" />
                      Pas de certificat disponible
                    </Button>
                  )}
                  
                  <Button
                    variant="outline"
                    className="border-blue-500/30 text-blue-400 hover:bg-blue-500/20"
                    onClick={() => {
                      setIsViewDetailsDialogOpen(false);
                      setNewStatus(selectedEnrollment.status);
                      setIsStatusDialogOpen(true);
                    }}
                  >
                    <Edit size={16} className="mr-2" />
                    Modifier le statut
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                    onClick={() => {
                      setIsViewDetailsDialogOpen(false);
                      setIsDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 size={16} className="mr-2" />
                    Supprimer l'inscription
                  </Button>
                </div>
              </div>
              
              {/* ID de l'inscription */}
              <div className="text-center text-xs text-white/50">
                ID de l'inscription: <span className="font-mono">{selectedEnrollment.id}</span>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsViewDetailsDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/10"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Boîte de dialogue pour modifier le statut */}
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent className="bg-gray-900 text-white border-white/10">
          <DialogHeader>
            <DialogTitle>Modifier le statut de l'inscription</DialogTitle>
            <DialogDescription className="text-white/70">
              Choisissez un nouveau statut pour cette inscription.
            </DialogDescription>
          </DialogHeader>
          
          {selectedEnrollment && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-2">Étudiant: {selectedEnrollment.student_name}</h4>
                <h4 className="text-sm font-medium mb-4">Cours: {selectedEnrollment.course?.title || selectedEnrollment.course_title}</h4>
              </div>
              
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="bg-gray-800 border-white/10 text-white">
                  <SelectValue placeholder="Sélectionner un statut" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 text-white border-white/10">
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="completed">Terminé</SelectItem>
                  <SelectItem value="cancelled">Annulé</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsStatusDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/10"
            >
              Annuler
            </Button>
            <Button 
              onClick={() => {
                if (selectedEnrollment && newStatus) {
                  handleUpdateStatus(selectedEnrollment.id, newStatus);
                  setIsStatusDialogOpen(false);
                }
              }}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Boîte de dialogue pour confirmer la suppression */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="bg-gray-900 text-white border-white/10">
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription className="text-white/70">
              Êtes-vous sûr de vouloir supprimer cette inscription ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          
          {selectedEnrollment && (
            <div className="space-y-2 py-2">
              <p><strong>Étudiant:</strong> {selectedEnrollment.student_name}</p>
              <p><strong>Cours:</strong> {selectedEnrollment.course?.title || selectedEnrollment.course_title}</p>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/10"
            >
              Annuler
            </Button>
            <Button 
              onClick={() => {
                if (selectedEnrollment) {
                  handleDeleteEnrollment(selectedEnrollment.id);
                  setIsDeleteDialogOpen(false);
                }
              }}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Boîte de dialogue pour afficher les détails de l'étudiant */}
      <Dialog open={isStudentDetailsDialogOpen} onOpenChange={setIsStudentDetailsDialogOpen}>
        <DialogContent className="bg-gray-900 text-white border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle>Informations détaillées de l'étudiant</DialogTitle>
            <DialogDescription className="text-white/70">
              Toutes les informations disponibles sur le profil de l'étudiant.
            </DialogDescription>
          </DialogHeader>
          
          {studentDetails ? (
            <div className="space-y-6">
              {/* Informations principales */}
              <div className="bg-black/20 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-red-400">Informations personnelles</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-white/70">Nom complet</h4>
                    <p className="text-white text-lg">{studentDetails.full_name}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-white/70">Email</h4>
                    <p className="text-white">{studentDetails.email}</p>
                  </div>
                  {studentDetails.phone && (
                    <div>
                      <h4 className="text-sm font-medium text-white/70">Téléphone</h4>
                      <p className="text-white">{studentDetails.phone}</p>
                    </div>
                  )}
                  <div>
                    <h4 className="text-sm font-medium text-white/70">ID Étudiant</h4>
                    <p className="text-white text-xs font-mono">{studentDetails.id}</p>
                  </div>
                </div>
              </div>
              
              {/* Adresse */}
              {(studentDetails.address || studentDetails.city || studentDetails.country) && (
                <div className="bg-black/20 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3 text-red-400">Adresse</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {studentDetails.address && (
                      <div>
                        <h4 className="text-sm font-medium text-white/70">Adresse</h4>
                        <p className="text-white">{studentDetails.address}</p>
                      </div>
                    )}
                    {studentDetails.city && (
                      <div>
                        <h4 className="text-sm font-medium text-white/70">Ville</h4>
                        <p className="text-white">{studentDetails.city}</p>
                      </div>
                    )}
                    {studentDetails.country && (
                      <div>
                        <h4 className="text-sm font-medium text-white/70">Pays</h4>
                        <p className="text-white">{studentDetails.country}</p>
                      </div>
                    )}
                    {studentDetails.postal_code && (
                      <div>
                        <h4 className="text-sm font-medium text-white/70">Code postal</h4>
                        <p className="text-white">{studentDetails.postal_code}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Informations techniques */}
              <div className="bg-black/20 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-red-400">Informations techniques</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-white/70">Date de création</h4>
                    <p className="text-white">{formatDate(studentDetails.created_at)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-white/70">Dernière mise à jour</h4>
                    <p className="text-white">{formatDate(studentDetails.updated_at)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-white/70">ID Tenant</h4>
                    <p className="text-white text-xs font-mono">{studentDetails.tenant_id}</p>
                  </div>
                  {studentDetails.token_expires_at && (
                    <div>
                      <h4 className="text-sm font-medium text-white/70">Expiration du token</h4>
                      <p className="text-white">{formatDate(studentDetails.token_expires_at)}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Préférences */}
              {studentDetails.preferences && Object.keys(studentDetails.preferences).length > 0 && (
                <div className="bg-black/20 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3 text-red-400">Préférences</h3>
                  <pre className="bg-black/30 p-3 rounded text-xs overflow-auto max-h-40">
                    {JSON.stringify(studentDetails.preferences, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-white/70">
              <Clock className="mx-auto h-10 w-10 mb-4 animate-spin text-blue-400" />
              <p>Chargement des informations de l'étudiant...</p>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsStudentDetailsDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/10"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Boîte de dialogue pour ajouter une nouvelle inscription */}
      <Dialog open={isAddEnrollmentDialogOpen} onOpenChange={setIsAddEnrollmentDialogOpen}>
        <DialogContent className="bg-gray-900 text-white border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nouvelle inscription</DialogTitle>
            <DialogDescription className="text-white/70">
              Inscrivez un étudiant à un cours en remplissant le formulaire ci-dessous.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Sélection de l'étudiant */}
            <div className="space-y-2">
              <Label htmlFor="student" className="text-white">Étudiant</Label>
              <Select 
                value={newEnrollmentData.student_id} 
                onValueChange={(value) => setNewEnrollmentData({...newEnrollmentData, student_id: value})}
              >
                <SelectTrigger className="bg-gray-800 border-white/10 text-white">
                  <SelectValue placeholder="Sélectionner un étudiant" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 text-white border-white/10">
                  {availableStudents.length > 0 ? (
                    availableStudents.map(student => (
                      <SelectItem key={student.id} value={student.id}>
                        {student.full_name} ({student.email})
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-students" disabled>
                      Aucun étudiant disponible
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            {/* Sélection du cours */}
            <div className="space-y-2">
              <Label htmlFor="course" className="text-white">Cours</Label>
              <Select 
                value={newEnrollmentData.course_id} 
                onValueChange={(value) => setNewEnrollmentData({...newEnrollmentData, course_id: value})}
              >
                <SelectTrigger className="bg-gray-800 border-white/10 text-white">
                  <SelectValue placeholder="Sélectionner un cours" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 text-white border-white/10">
                  {availableCourses.length > 0 ? (
                    availableCourses.map(course => (
                      <SelectItem key={course.id} value={course.id}>
                        {course.title}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-courses" disabled>
                      Aucun cours disponible
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            {/* Sélection du statut */}
            <div className="space-y-2">
              <Label htmlFor="status" className="text-white">Statut initial</Label>
              <Select 
                value={newEnrollmentData.status} 
                onValueChange={(value) => setNewEnrollmentData({...newEnrollmentData, status: value})}
              >
                <SelectTrigger className="bg-gray-800 border-white/10 text-white">
                  <SelectValue placeholder="Sélectionner un statut" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 text-white border-white/10">
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="completed">Terminé</SelectItem>
                  <SelectItem value="suspended">Suspendu</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsAddEnrollmentDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/10"
            >
              Annuler
            </Button>
            <Button 
              onClick={() => {
                console.log('Bouton Créer inscription cliqué');
                handleCreateEnrollment();
              }}
              className="bg-red-600 text-white hover:bg-red-700"
              type="button"
            >
              Créer l'inscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminEnrollmentsPage;
